import React, { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../context/auth/AuthContext";
import Box from "@material-ui/core/Box";
import { MapPin, User, Phone, Trash2 } from "react-feather";
import { Button, Grid } from "@material-ui/core";
import Column from "../../components/Detail";
import EditProfile from "./EditProfile";
import EditAddress from "./EditAddress";
import DeleteInstaller from "./DeleteInstaller";
import { Request } from "../../api/installers/Installers.api";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import EditContacts from "./EditContacts";

const useStyles = makeStyles((theme) => ({
  "@keyframes fadeSlideIn": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  root: {
    animation: "$fadeSlideIn 0.3s ease-out",
  },
  sectionCard: {
    background: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    height: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 6px 20px rgba(0,0,0,0.09)",
      transform: "translateY(-2px)",
    },
  },
  editButton: {
    borderRadius: 8,
    fontWeight: 600,
    padding: "8px 20px",
    textTransform: "none",
    fontSize: "0.875rem",
    boxShadow: "none",
    marginTop: theme.spacing(2),
    transition: "box-shadow 0.2s ease",
    "&:hover": {
      boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
    },
  },
  deleteWrapper: {
    display: "flex",
    alignItems: "center",
    height: "100%",
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  deleteButton: {
    borderRadius: 8,
    fontWeight: 600,
    padding: "8px 20px",
    textTransform: "none",
    fontSize: "0.875rem",
    boxShadow: "none",
    background: theme.palette.error.main,
    color: "#fff",
    transition: "box-shadow 0.2s ease, background 0.2s ease",
    "&:hover": {
      background: theme.palette.error.dark,
      boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
    },
  },
}));

const Detail = ({ item }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const onRefresh = () => {
    Request.refreshInstallers(token);
  };

  const address = [
    { title: t("detail_installer.address.country"), value: item.profile.country },
    { title: t("detail_installer.address.district"), value: item.profile.district },
    { title: t("detail_installer.address.city"), value: item.profile.city },
    { title: t("detail_installer.address.street"), value: item.profile.street },
    { title: t("detail_installer.address.cap"), value: item.profile.cap },
  ];

  const user = [
    { title: t("detail_installer.user.first_name"), value: item.first_name },
    { title: t("detail_installer.user.last_name"), value: item.last_name },
    { title: t("detail_installer.user.email"), value: item.email },
    { title: t("detail_installer.user.id_installer"), value: item.profile.id_installer },
  ];

  const contacts = [
    { title: t("detail_installer.contacts.telephone"), value: item.profile.telephone_number },
    { title: t("detail_installer.contacts.mobile_phone"), value: item.profile.cellular_number },
    { title: t("detail_installer.contacts.vat_number"), value: item.profile.vat_number },
  ];

  const [openEditProfile, setOpenEditProfile] = useState(false);
  const [openEditAddress, setOpenEditAddress] = useState(false);
  const [openEditContacts, setOpenEditContacts] = useState(false);
  const [openDeleteInstaller, setOpenDeleteInstaller] = useState(false);

  useEffect(onRefresh, [openEditProfile, openEditAddress, openDeleteInstaller, openEditContacts]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Box ml={{ xs: 0, sm: 2 }} mr={{ xs: 0, sm: 2 }} mb={2} mt={2} className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Box className={classes.sectionCard}>
              <Column icon={<User />} data={user} />
              <Button
                className={classes.editButton}
                color="primary"
                variant="contained"
                onClick={() => setOpenEditProfile(true)}
              >
                {t("detail_installer.edit_profile")}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box className={classes.sectionCard}>
              <Column icon={<MapPin />} data={address} />
              <Button
                className={classes.editButton}
                color="primary"
                variant="contained"
                onClick={() => setOpenEditAddress(true)}
              >
                {t("detail_installer.edit_address")}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box className={classes.sectionCard}>
              <Column icon={<Phone />} data={contacts} />
              <Button
                className={classes.editButton}
                color="primary"
                variant="contained"
                onClick={() => setOpenEditContacts(true)}
              >
                {t("detail_installer.edit_contacts")}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box className={classes.deleteWrapper}>
              <Button
                className={classes.deleteButton}
                variant="contained"
                startIcon={<Trash2 size={16} />}
                onClick={() => setOpenDeleteInstaller(true)}
              >
                {t("detail_installer.delete_profile")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {openEditProfile && item ? (
        <EditProfile
          handleClose={() => setOpenEditProfile(false)}
          open={openEditProfile}
          profile={item}
        />
      ) : null}
      {openEditAddress && item ? (
        <EditAddress
          handleClose={() => setOpenEditAddress(false)}
          open={openEditAddress}
          address={item}
        />
      ) : null}
      {openEditContacts && item ? (
        <EditContacts
          handleClose={() => setOpenEditContacts(false)}
          open={openEditContacts}
          contacts={item}
          isInstaller={true}
        />
      ) : null}
      {openDeleteInstaller && item ? (
        <DeleteInstaller
          handleClose={() => setOpenDeleteInstaller(false)}
          open={openDeleteInstaller}
          profile={item}
        />
      ) : null}
    </>
  );
};

export default Detail;
